import { useEffect, useRef, useState } from "react"

function useReveal(threshold = 0.12) {
    const ref = useRef(null)
    const [visible, setVisible] = useState(false)
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } },
            { threshold }
        )
        obs.observe(el)
        return () => obs.disconnect()
    }, [threshold])
    return [ref, visible]
}

export function Reveal({ children, delay = 0, from = "bottom", style = {} }) {
    const [ref, visible] = useReveal()
    const hidden = from === "bottom" ? "translateY(50px)" : from === "left" ? "translateX(-40px)" : "translateX(40px)"
    return (
        <div ref={ref} style={{
            transition: `opacity .75s ${delay}ms cubic-bezier(.22,1,.36,1), transform .75s ${delay}ms cubic-bezier(.22,1,.36,1)`,
            opacity: visible ? 1 : 0,
            transform: visible ? "none" : hidden,
            ...style,
        }}>
            {children}
        </div>
    )
}

export function Counter({ to, duration = 1600, prefix = "", suffix = "" }) {
    const [val, setVal] = useState(0)
    const [ref, visible] = useReveal(0.5)
    useEffect(() => {
        if (!visible) return
        const start = performance.now()
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1)
            setVal(Math.round((1 - Math.pow(1 - p, 4)) * to))
            if (p < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
    }, [visible, to, duration])
    return <span ref={ref}>{prefix}{val.toLocaleString("en-IN")}{suffix}</span>
}

/* Animated canvas — drifting orbs + grid + shooting stars */

export const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{background:#08080f}
.cb{font-family:'Inter',sans-serif;background:transparent;color:#fff;min-height:100vh;overflow-x:hidden;position:relative;z-index:1}

/* ── particles ── */
.ptcl{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:1}
.p{position:absolute;border-radius:50%;animation:rise linear infinite}
@keyframes rise{0%{transform:translateY(0) scale(1);opacity:.6}100%{transform:translateY(-110vh) scale(.15);opacity:0}}

/* ── hero ── */
.hero{position:relative;min-height:100svh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:120px 40px 100px;overflow:hidden}
.hero>*{position:relative;z-index:2}

/* aurora layers behind hero text */
.aurora{position:absolute;inset:0;z-index:1;overflow:hidden;pointer-events:none}
.aurora-band{position:absolute;border-radius:50%;filter:blur(90px);animation:auroraDrift ease-in-out infinite}
@keyframes auroraDrift{0%,100%{transform:translateX(0) translateY(0) scale(1)}33%{transform:translateX(4%) translateY(-3%) scale(1.04)}66%{transform:translateX(-3%) translateY(2%) scale(0.97)}}

.chip{display:inline-flex;align-items:center;gap:8px;background:rgba(255,230,0,.1);border:1.5px solid rgba(255,230,0,.35);border-radius:100px;padding:8px 22px;font-size:.75rem;font-weight:700;letter-spacing:.1em;color:#ffe600;text-transform:uppercase;margin-bottom:36px;animation:popIn .7s .1s both cubic-bezier(.34,1.56,.64,1);backdrop-filter:blur(10px)}
@keyframes popIn{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:scale(1)}}

.hero h1{font-size:clamp(3.2rem,9vw,8rem);font-weight:900;line-height:.94;letter-spacing:-.045em;margin-bottom:30px;animation:up .9s .25s both cubic-bezier(.22,1,.36,1)}
.hero h1 em{font-style:normal;color:#ffe600;display:block}
.hero h1 .dim{color:rgba(255,255,255,.18)}

.hero-sub{font-size:clamp(1rem,2vw,1.25rem);color:rgba(255,255,255,.48);max-width:520px;line-height:1.7;margin-bottom:48px;animation:up .9s .4s both cubic-bezier(.22,1,.36,1)}
@keyframes up{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:none}}

.hero-btns{display:flex;gap:16px;flex-wrap:wrap;justify-content:center;animation:up .9s .55s both cubic-bezier(.22,1,.36,1)}

.btn-primary{display:inline-block;padding:17px 40px;border-radius:100px;font-size:1.05rem;font-weight:800;letter-spacing:.01em;text-decoration:none;background:linear-gradient(135deg,#ffe600,#ffb800);color:#000;cursor:pointer;transition:transform .25s cubic-bezier(.34,1.56,.64,1),box-shadow .25s;box-shadow:0 4px 30px rgba(255,200,0,.25),0 0 0 0 rgba(255,230,0,0)}
.btn-primary:hover{transform:scale(1.07) translateY(-3px);box-shadow:0 16px 48px rgba(255,200,0,.4),0 0 60px rgba(255,230,0,.15)}

.btn-ghost{display:inline-block;padding:16px 38px;border-radius:100px;font-size:1.05rem;font-weight:700;text-decoration:none;background:rgba(255,255,255,.07);color:rgba(255,255,255,.72);border:1.5px solid rgba(255,255,255,.14);cursor:pointer;backdrop-filter:blur(10px);transition:all .25s ease}
.btn-ghost:hover{background:rgba(255,255,255,.13);color:#fff;border-color:rgba(255,255,255,.3)}

.scroll-hint{position:absolute;bottom:32px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:6px;animation:fadeIn 1s 1.6s both;z-index:2}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.scroll-line{width:1.5px;height:44px;background:linear-gradient(to bottom,rgba(255,230,0,.9),transparent);animation:lineFlow 2s ease-in-out infinite}
@keyframes lineFlow{0%{transform:scaleY(0);transform-origin:top;opacity:0}50%{opacity:1}100%{transform:scaleY(1);transform-origin:top;opacity:0}}

/* ── spots banner ── */
.spots-bar{background:rgba(255,230,0,.06);border-top:1px solid rgba(255,230,0,.12);border-bottom:1px solid rgba(255,230,0,.12);padding:16px 40px;text-align:center;font-size:.85rem;font-weight:700;color:rgba(255,255,255,.5);letter-spacing:.03em;position:relative;z-index:2}
.spots-bar strong{color:#ffe600}

/* ── full-width sections ── */
.sec{padding:100px 60px;position:relative;overflow:hidden}
.sec-inner{max-width:1400px;margin:0 auto}

/* section bg glow */
.sec-glow{position:absolute;border-radius:50%;filter:blur(120px);pointer-events:none;z-index:0}

.sec-eyebrow{font-size:.72rem;font-weight:800;letter-spacing:.22em;text-transform:uppercase;color:#ffe600;margin-bottom:16px}
.sec-title{font-size:clamp(2.4rem,5vw,4rem);font-weight:900;letter-spacing:-.04em;line-height:1.02;margin-bottom:22px}
.sec-body{font-size:1.05rem;color:rgba(255,255,255,.48);line-height:1.72;max-width:520px;margin-bottom:56px}

/* ── stats bar ── */
.stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:rgba(255,255,255,.06);border-top:1px solid rgba(255,255,255,.06);border-bottom:1px solid rgba(255,255,255,.06);position:relative;z-index:2}
.stat-cell{padding:32px 24px;text-align:center;background:#08080f;transition:background .3s}
.stat-cell:hover{background:rgba(255,255,255,.04)}
.stat-val{font-size:2.4rem;font-weight:900;color:#ffe600;letter-spacing:-.04em;line-height:1}
.stat-lbl{font-size:.75rem;font-weight:600;color:rgba(255,255,255,.38);margin-top:6px;text-transform:uppercase;letter-spacing:.08em}

/* ── duties ── */
.duty-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:20px}
.duty{border-radius:22px;padding:36px 30px;background:rgba(255,255,255,.04);border:1.5px solid rgba(255,255,255,.08);transition:all .4s cubic-bezier(.22,1,.36,1);cursor:default;position:relative;overflow:hidden}
.duty::after{content:'';position:absolute;inset:0;border-radius:inherit;background:radial-gradient(ellipse at 30% 0%,rgba(255,230,0,.07),transparent 60%);opacity:0;transition:opacity .4s}
.duty:hover{border-color:rgba(255,230,0,.3);transform:translateY(-8px);box-shadow:0 32px 80px rgba(0,0,0,.5)}
.duty:hover::after{opacity:1}
.duty-icon{font-size:2.4rem;margin-bottom:20px;display:block;transition:transform .4s cubic-bezier(.34,1.56,.64,1)}
.duty:hover .duty-icon{transform:scale(1.2) rotate(-6deg)}
.duty-title{font-size:1.1rem;font-weight:800;margin-bottom:12px}
.duty-body{font-size:.88rem;color:rgba(255,255,255,.48);line-height:1.7}

/* ── engine ── */
.engine-wrap{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.eng-card{border-radius:24px;padding:48px 40px;text-align:center;border:1.5px solid rgba(255,255,255,.1);position:relative;overflow:hidden;transition:transform .35s ease,box-shadow .35s ease}
.eng-card:hover{transform:translateY(-8px)}
.eng-glow{position:absolute;inset:-40%;border-radius:50%;opacity:.14;filter:blur(50px);pointer-events:none;animation:orbPulse 4s ease-in-out infinite}
@keyframes orbPulse{0%,100%{transform:scale(1);opacity:.14}50%{transform:scale(1.15);opacity:.22}}
.eng-val{font-size:5rem;font-weight:900;letter-spacing:-.05em;line-height:1}
.eng-label{font-size:1.1rem;font-weight:700;margin:14px 0 8px}
.eng-desc{font-size:.88rem;opacity:.45;line-height:1.6}

/* ── tiers ── */
.tier-list{display:flex;flex-direction:column;gap:12px}
.tier{display:grid;grid-template-columns:64px 1fr auto;align-items:center;border-radius:18px;background:rgba(255,255,255,.04);border:1.5px solid rgba(255,255,255,.08);overflow:hidden;cursor:pointer;transition:border-color .3s,background .3s,transform .3s cubic-bezier(.22,1,.36,1);position:relative}
.tier::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--c);opacity:.35;transition:opacity .3s,width .3s}
.tier:hover::before,.tier.open::before{opacity:1;width:4px}
.tier:hover{background:rgba(255,255,255,.07);transform:translateX(4px)}
.tier.open{border-color:var(--c);background:rgba(255,255,255,.06)}
.tier-idx{padding:24px 0 24px 24px;font-size:1.5rem;font-weight:900;opacity:.18;color:var(--c);font-variant-numeric:tabular-nums;transition:opacity .3s}
.tier.open .tier-idx,.tier:hover .tier-idx{opacity:.5}
.tier-mid{padding:22px 20px}
.tier-name{font-size:1.05rem;font-weight:800;margin-bottom:4px}
.tier-pers{font-size:.76rem;font-weight:600;opacity:.38;text-transform:uppercase;letter-spacing:.07em}
.tier-right{padding:22px 24px 22px 0;text-align:right;min-width:110px}
.tier-cash{font-size:1.6rem;font-weight:900;color:var(--c)}
.tier-refs{font-size:.72rem;opacity:.38;margin-top:4px}
.tier-drawer{grid-column:1/-1;font-size:.9rem;color:rgba(255,255,255,.52);line-height:1.65;padding:0 24px;max-height:0;overflow:hidden;transition:max-height .5s cubic-bezier(.22,1,.36,1),padding .5s ease,opacity .4s ease;opacity:0}
.tier.open .tier-drawer{max-height:100px;padding:0 24px 22px;opacity:1}


/* ── perks ── */
.perk-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.perk{border-radius:20px;padding:30px 26px;border:1.5px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);transition:all .35s cubic-bezier(.22,1,.36,1);cursor:default}
.perk:hover{border-color:rgba(255,230,0,.3);background:rgba(255,255,255,.07);transform:translateY(-6px);box-shadow:0 24px 60px rgba(0,0,0,.4)}
.perk-e{font-size:2.2rem;margin-bottom:16px;display:block}
.perk-t{font-size:.98rem;font-weight:800;margin-bottom:8px}
.perk-s{font-size:.82rem;color:rgba(255,255,255,.42);line-height:1.6}

/* ── final cta ── */
.cta-sec{padding:100px 40px 120px;text-align:center;position:relative;overflow:hidden}
.cta-sec>*{position:relative;z-index:1}
.cta-bg{position:absolute;inset:0;background:radial-gradient(ellipse 70% 90% at 50% 100%,rgba(85,34,125,.55) 0%,transparent 70%);z-index:0}
.cta-sec h2{font-size:clamp(2.5rem,7vw,5rem);font-weight:900;letter-spacing:-.045em;line-height:.97;margin-bottom:20px}
.cta-sec h2 em{font-style:normal;color:#ffe600}
.cta-sec p{font-size:1rem;color:rgba(255,255,255,.42);margin-bottom:48px;line-height:1.65}

.live-dot{display:inline-flex;align-items:center;gap:10px;margin-bottom:28px;font-size:.85rem;font-weight:700;color:rgba(255,255,255,.45)}
.dot{width:9px;height:9px;border-radius:50%;background:#22c55e;box-shadow:0 0 10px #22c55e;animation:livePulse 2s ease-in-out infinite}
@keyframes livePulse{0%,100%{box-shadow:0 0 5px #22c55e}50%{box-shadow:0 0 18px #22c55e,0 0 36px rgba(34,197,94,.35)}}

/* ── divider ── */
.div{height:1px;background:linear-gradient(to right,transparent,rgba(255,255,255,.07),transparent)}

@media(max-width:1024px){
    .duty-grid,.perk-grid{grid-template-columns:repeat(2,1fr)}
    .stats-row{grid-template-columns:repeat(2,1fr)}
}
@media(max-width:680px){
    .sec{padding:70px 24px}
    .duty-grid,.perk-grid,.engine-wrap{grid-template-columns:1fr}
    .stats-row{grid-template-columns:repeat(2,1fr)}
    .tier{grid-template-columns:48px 1fr}
    .tier-right{display:none}
    .bonus{flex-direction:column;gap:16px;padding:26px 24px}
}
`

export function BackgroundCanvas() {
    const canvasRef = useRef(null)
    useEffect(() => {
        const canvas = canvasRef.current
        const ctx = canvas.getContext("2d")
        let raf

        const isMobile = window.innerWidth < 768

        const resize = () => {
            canvas.width = window.innerWidth
            canvas.height = window.innerHeight
        }
        resize()
        window.addEventListener("resize", resize)

        const orbDefs = [
            { x: 0.12, y: 0.08, r: 380, color: "rgba(85,34,125,",  spd: 0.00016, phase: 0 },
            { x: 0.88, y: 0.12, r: 300, color: "rgba(255,200,0,",   spd: 0.00022, phase: 2.1 },
            { x: 0.5,  y: 0.32, r: 420, color: "rgba(50,15,100,",   spd: 0.00012, phase: 4.2 },
            { x: 0.78, y: 0.55, r: 260, color: "rgba(120,50,200,",  spd: 0.0002,  phase: 1.5 },
            { x: 0.08, y: 0.65, r: 280, color: "rgba(255,160,0,",   spd: 0.00014, phase: 3.3 },
            { x: 0.55, y: 0.8,  r: 350, color: "rgba(70,20,130,",   spd: 0.00018, phase: 5.1 },
            { x: 0.3,  y: 0.92, r: 240, color: "rgba(255,200,0,",   spd: 0.0002,  phase: 0.8 },
        ]
        const orbs = isMobile ? orbDefs.slice(0, 3) : orbDefs

        const starCount = isMobile ? 50 : 140
        const stars = Array.from({ length: starCount }, () => ({
            x: Math.random(), y: Math.random(),
            r: 0.4 + Math.random() * 1.4,
            spd: 0.0003 + Math.random() * 0.0007,
            phase: Math.random() * Math.PI * 2,
        }))

        const shootCount = isMobile ? 2 : 7
        const shoots = Array.from({ length: shootCount }, () => mkShoot())
        function mkShoot() {
            return {
                x: Math.random(), y: Math.random() * 0.45,
                len: 0.05 + Math.random() * 0.09,
                spd: 0.0007 + Math.random() * 0.001,
                life: 0, maxLife: 0.5 + Math.random() * 1,
                angle: Math.PI / 4 + (Math.random() - 0.5) * 0.4,
                active: Math.random() > 0.65, delay: Math.random() * 10,
            }
        }

        const gridCols = isMobile ? 14 : 28
        // on mobile skip dot rendering for the grid
        const drawDots = !isMobile

        // throttle to ~30fps on mobile
        const fpsInterval = isMobile ? 33 : 16
        let lastFrame = 0

        let t = 0
        const draw = (timestamp) => {
            raf = requestAnimationFrame(draw)
            if (timestamp - lastFrame < fpsInterval) return
            lastFrame = timestamp

            const W = canvas.width, H = canvas.height
            ctx.clearRect(0, 0, W, H)
            t += fpsInterval / 1000

            orbs.forEach(o => {
                const px = (o.x + Math.sin(t * o.spd * 1000 + o.phase) * 0.09) * W
                const py = (o.y + Math.cos(t * o.spd * 800 + o.phase) * 0.07) * H
                const g = ctx.createRadialGradient(px, py, 0, px, py, o.r)
                g.addColorStop(0, o.color + "0.16)")
                g.addColorStop(0.5, o.color + "0.06)")
                g.addColorStop(1, o.color + "0)")
                ctx.fillStyle = g
                ctx.beginPath(); ctx.arc(px, py, o.r, 0, Math.PI * 2); ctx.fill()
            })

            /* grid */
            const cols = gridCols
            const cw = W / cols, ch = cw
            ctx.lineWidth = 1
            for (let x = 0; x <= cols; x++) {
                ctx.strokeStyle = `rgba(255,255,255,${0.015 + 0.01 * Math.sin(t * 0.3 + x * 0.2)})`
                ctx.beginPath(); ctx.moveTo(x * cw, 0); ctx.lineTo(x * cw, H); ctx.stroke()
            }
            for (let y = 0; y * ch < H; y++) {
                ctx.strokeStyle = `rgba(255,255,255,${0.015 + 0.01 * Math.sin(t * 0.3 + y * 0.2)})`
                ctx.beginPath(); ctx.moveTo(0, y * ch); ctx.lineTo(W, y * ch); ctx.stroke()
            }
            if (drawDots) {
                for (let x = 0; x <= cols; x++) {
                    for (let y = 0; y * ch < H; y++) {
                        const pulse = 0.04 + 0.04 * Math.sin(t * 0.8 + x * 0.4 + y * 0.4)
                        ctx.fillStyle = `rgba(255,255,255,${pulse})`
                        ctx.beginPath(); ctx.arc(x * cw, y * ch, 1.2, 0, Math.PI * 2); ctx.fill()
                    }
                }
            }

            /* stars */
            stars.forEach(s => {
                const a = 0.15 + 0.55 * (0.5 + 0.5 * Math.sin(t * s.spd * 1000 + s.phase))
                ctx.fillStyle = `rgba(255,255,255,${a})`
                ctx.beginPath(); ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2); ctx.fill()
            })

            /* shooting stars */
            shoots.forEach((s, i) => {
                if (!s.active) { s.delay -= fpsInterval / 1000; if (s.delay <= 0) { s.active = true; s.life = 0 }; return }
                s.life += fpsInterval / 1000
                if (s.life > s.maxLife) { shoots[i] = mkShoot(); shoots[i].active = true; return }
                const prog = s.life / s.maxLife
                const px = (s.x + Math.cos(s.angle) * s.spd * s.life * 1000) * W
                const py = (s.y + Math.sin(s.angle) * s.spd * s.life * 1000) * H
                const alpha = Math.sin(prog * Math.PI) * 0.95
                const ex = px - Math.cos(s.angle) * s.len * W
                const ey = py - Math.sin(s.angle) * s.len * H
                const g = ctx.createLinearGradient(ex, ey, px, py)
                g.addColorStop(0, "rgba(255,255,255,0)")
                g.addColorStop(1, `rgba(255,240,160,${alpha})`)
                ctx.strokeStyle = g; ctx.lineWidth = 1.8
                ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(px, py); ctx.stroke()
                const hg = ctx.createRadialGradient(px, py, 0, px, py, 7)
                hg.addColorStop(0, `rgba(255,240,160,${alpha * 0.8})`); hg.addColorStop(1, "rgba(255,240,160,0)")
                ctx.fillStyle = hg; ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2); ctx.fill()
            })
        }
        raf = requestAnimationFrame(draw)
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize) }
    }, [])
    return (
        <canvas ref={canvasRef} style={{
            position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
            zIndex: 0, pointerEvents: "none",
        }} />
    )
}
